import {useCallback, useMemo} from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import { DateTimeFormatter } from "../../common/DateTimeFormatter";
import {IsdsMessageFilterDto} from "../../client/message/IsdsMessageApiClient";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";
import {MessageStateTranslKey} from "./MessageStateTranslKey";
import {MessageStatusFormatter} from "../message/MessageStatusFormatter";
import {IsdsMessageStateDto, IsdsMessageStateFilterDto} from "../../client/message-state/IsdsMessageStateApiClient";
import {FilterSearchType} from "./MessageStateSearchPanel";

function buildMessageGridColumns(t: (key: string) => string): GridColDef[] {
    return [
        {
            field: "messageId",
            headerName: t(MessageStateTranslKey.ID),
            width: 300
        },
        {
            field: "changeTime",
            headerName: t(MessageStateTranslKey.CHANGE_TIME),
            width: 200,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
        {
            field: "type",
            headerName: t(MessageStateTranslKey.STATE),
            width: 150,
            valueGetter: params => MessageStatusFormatter.formatMessageStatusStatus(params.row.type, t)
        },
        {
            field: "statusCode",
            headerName: t(MessageStateTranslKey.STATUS_CODE),
            width: 150,
        },
        {
            field: "errorCode",
            headerName: t(MessageStateTranslKey.ERROR_CODE),
            width: 150,
        },
        {
            field: "errorMessage",
            headerName: t(MessageStateTranslKey.ERROR_MESSAGE),
            width: 800,
        },
    ];
}

export interface MessageStateGridProps {
    rowsData: IsdsMessageStateDto[];
    totalMessageCount: number;

    nextPageToken?: string | null;
    messageFilter: IsdsMessageStateFilterDto;
    fetchMessages: (filter: IsdsMessageStateFilterDto, searchType: FilterSearchType) => void;
}

export default function MessageStateGrid(props: Readonly<MessageStateGridProps>) {
    const {rowsData, totalMessageCount, nextPageToken, messageFilter, fetchMessages} = props;

    const {t} = useTranslation();
    const columns = useMemo(() => buildMessageGridColumns(t), [t]);

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: IsdsMessageFilterDto = {
            ...messageFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchMessages(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, messageFilter, fetchMessages]);

    return (
        <PageableGrid
            rowsData={rowsData}
            totalRowsCount={totalMessageCount}
            columns={columns}
            onFetchMore={handleFetchMore}
        />
    );
}
